import { type CSSProperties, Suspense, useLayoutEffect, useRef } from "react";
import Video from "@/components/player/VideoJs";
import ShopWindow from "@/components/shopwindow/ShopWindow";
import { renderSplitLayout } from "@/components/player/experienceSplitLayout";
import ShopWindowHeader from "@/components/shopwindow/ShopWindowHeader";
import ControlBar from "@/components/player/ControlBar";
import Spinner from "@/components/player/Spinner";
import { valueNames, eventNames } from "@/modules/Constants";
import { McpChunk } from "@/components/mcp/mcpChunk";
import { isMcpDesktopLayout, isMcpLandscapeLayout } from "@/modules/mcpSurface";
import { renderImmersiveLayout } from "@/components/player/experienceImmersiveLayout";
import {
  computeExperienceView,
  type ExperienceLayoutContext,
  type OnApplicationEvent,
  PIXIE_SWITCH_CLEARANCE_PX,
} from "@/components/player/experienceView";
import * as playerRegistry from "@/modules/playerRegistry";
import { findAskConfig } from "@/modules/pixieWorkspace";
import { usePlayerPixieHost } from "@/modules/playerPixieHost";
import { useAutoFullscreen } from "@/components/player/useAutoFullscreen";
import PixieWorkspace from "@/components/shopwindow/ask/PixieWorkspace";
import { usePixieWorkspacePhase } from "@/components/player/usePixieWorkspacePhase";
import type { ExperienceState } from "@/types/state";

type ExperienceProps = {
  experienceState: ExperienceState;
  onApplicationEvent: OnApplicationEvent;
};

export default function Experience({
  experienceState,
  onApplicationEvent,
}: ExperienceProps) {
  const { phase: pixieWorkspacePhase, pixie } = usePixieWorkspacePhase();
  const fs = useAutoFullscreen();
  const workspaceHost = usePlayerPixieHost({
    experienceState,
    onApplicationEvent,
    config: findAskConfig(experienceState.applicationData) || undefined,
    layout: "workspace",
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const videoPanelRef = useRef<HTMLDivElement>(null);
  const favoritesRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const actionSheetRef = useRef<HTMLDivElement>(null);
  const landscapeSheetRef = useRef<HTMLDivElement>(null);

  // Auto-fullscreen on rotate to landscape (Android). Chrome allows
  // Element.requestFullscreen() WITHOUT a tap when it is called synchronously from
  // the orientation-change event for a user-generated rotation (WHATWG Fullscreen:
  // "transient activation OR triggered by a user generated orientation change").
  // So we listen to the orientation-change event DIRECTLY here — NOT the
  // resize-derived orientationManager, which is a separate event that does NOT
  // qualify. Attached on the stable Experience parent (the VideoJs child remounts
  // across a rotate); window/screen listeners are unaffected by that remount.
  // componentDidMount / componentWillUnmount (layout phase, as the class ran).
  useLayoutEffect(() => {
    fs.addOrientationListener();
    // Set the notch side for the current orientation (covers loading already in
    // landscape); kept in sync from the orientation-change handler.
    fs.scheduleNotchUpdate();

    return () => {
      fs.removeOrientationListener();
      pixie.clearClosedTimer();
      fs.clearNotch();
      // Experience is the STABLE parent — only <Video> remounts on a rotate
      // (see the note above), so this only runs on a real teardown or a chain
      // advance (which flips the store to LOADING, unmounting Experience).
      // Dispose the persistent player parked by VideoJs so it can't leak or be
      // adopted by the next (different) experience.
      playerRegistry.dispose();
    };
  }, [fs, pixie]);

  function renderPixieWorkspace(
    askOpen: boolean,
    style: CSSProperties | null,
    compact?: boolean,
  ) {
    return (
      <PixieWorkspace
        host={workspaceHost}
        open={askOpen}
        compact={compact}
        style={Object.assign(
          { "--pixie-switch-clearance": PIXIE_SWITCH_CLEARANCE_PX },
          style,
        )}
        onExit={pixie.onExit}
        onExited={pixie.onExited}
      />
    );
  }

  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;

  // MCP App surface (ChatGPT / Claude card): render the dedicated tabbed
  // layout, checked before the viewport-based branches so the card never falls
  // into them. DESKTOP-WIDTH CARDS ONLY — on a narrow/mobile card the player
  // keeps its existing layout (no new MCP UI), matching the shell's 600px
  // mobile/desktop card boundary (MOBILE_MAX_WIDTH_PX in app/mcp-app). Here we
  // detect the param the shell already appends.
  if (isMcpDesktopLayout()) {
    return (
      <Suspense fallback={<Spinner experienceState={experienceState} />}>
        <McpChunk.Component
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      </Suspense>
    );
  }

  // MCP landscape/tabs layout: the classic MCP surface renders the
  // video-first landscape view with the Method/Ingredients/Equipment tabs
  // UNDER the video (no LandscapeNav icons overlaid on the picture). Reuses
  // McpPlayer in its `landscape` variant. Checked before the viewport branches
  // so the MCP card never falls into the base immersive/split layouts.
  if (isMcpLandscapeLayout()) {
    return (
      <Suspense fallback={<Spinner experienceState={experienceState} />}>
        <McpChunk.Component
          landscape
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      </Suspense>
    );
  }

  const view = computeExperienceView(experienceState, pixieWorkspacePhase);
  const { isPortrait, isLoaded, askConfig, panelOpen, useImmersive } = view;

  const header = isPortrait ? (
    <ShopWindowHeader
      src={experienceData.getLogoImageUrl()}
      hasExitButton={applicationData.hasMobileExit()}
      exitLabel="Close"
      onExit={region =>
        onApplicationEvent({
          name: eventNames.EXIT,
          region: region,
        })
      }
    />
  ) : null;

  const shopWindow = isLoaded ? (
    <ShopWindow
      hasHeader={isPortrait === false}
      hasExitButton={false}
      askInWorkspace
      experienceState={experienceState}
      onApplicationEvent={onApplicationEvent}
    />
  ) : null;

  const video = (
    <Video
      experienceState={experienceState}
      timelineName={applicationData.getValue(valueNames.VIDEO_TIMELINE_NAME)}
      onApplicationEvent={onApplicationEvent}
    />
  );

  const controlBar = view.hasControlBar ? (
    <ControlBar
      showPixieButton={
        !!askConfig && !isPortrait && !useImmersive && !panelOpen
      }
      experienceState={experienceState}
      onApplicationEvent={onApplicationEvent}
    />
  ) : null;

  const ctx: ExperienceLayoutContext = {
    view,
    experienceState,
    onApplicationEvent,
    setContainerEl: fs.setContainerEl,
    header,
    shopWindow,
    video,
    controlBar,
    refs: {
      panel: panelRef,
      videoPanel: videoPanelRef,
      favorites: favoritesRef,
      list: listRef,
      actionSheet: actionSheetRef,
      landscapeSheet: landscapeSheetRef,
    },
    renderPixieWorkspace,
  };

  // Portrait AND desktop landscape both render the split/stacked layout;
  // only mobile landscape gets the immersive branch.
  if (!useImmersive) {
    return renderSplitLayout(ctx);
  }
  return renderImmersiveLayout(ctx);
}
